// src/pages/home/Login.js
import { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import Navbar from "../../components/Navbar";
import { resolveAssetUrl } from "../../api/config";
import API from "../../api/api";
import Swal from "sweetalert2";
import {
  FaSpinner,
  FaEnvelope,
  FaLock,
  FaRunning,
  FaUserShield,
  FaArrowRight,
  FaTimes,
  FaUserCircle,
  FaKey,
  FaCheckCircle,
  FaArrowLeft,
} from "react-icons/fa";

// eslint-disable-next-line no-unused-vars
const resolvePhoto = (photoPath) => {
  if (!photoPath) return null;
  return resolveAssetUrl(photoPath);
};

const Login = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [roleSelectionData, setRoleSelectionData] = useState(null);
  const [loginError, setLoginError] = useState("");

  const navigateForRole = (role) => {
    const normalized = String(role || "").toLowerCase();
    if (normalized === "coach") navigate("/coach");
    else if (normalized === "manager") navigate("/manager");
    else if (normalized === "camp_official") navigate("/camp-official");
    else if (["regular_manager", "regular_official"].includes(normalized)) navigate("/staff");
    else if (["admin", "secretary"].includes(normalized)) navigate("/admin");
    else if (normalized === "district") navigate("/district");
    else if (normalized === "player") navigate("/player");
    else navigate("/");
  };

  // Forgot Password modal states
  const [forgotModalOpen, setForgotModalOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1: request OTP, 2: verify & reset
  const [forgotIdentifier, setForgotIdentifier] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMaskedEmail, setForgotMaskedEmail] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");

  const handleSendForgotOtp = async (e) => {
    if (e) e.preventDefault();
    if (!forgotIdentifier.trim()) {
      setForgotError("Please enter your registered Email, Mobile, or Registration ID.");
      return;
    }
    setForgotLoading(true);
    setForgotError("");
    setForgotSuccess("");
    try {
      const res = await API.sendForgotPasswordOtp(forgotIdentifier.trim());
      const data = res?.data || res;
      if (data?.success) {
        setForgotEmail(data.email || "");
        setForgotMaskedEmail(data.masked_email || data.email || "");
        setForgotStep(2);
        setForgotSuccess(data.message || "OTP sent successfully to your registered email.");
      } else {
        setForgotError(data?.message || "Account not found or OTP sending failed.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to send OTP. Please check your details.";
      setForgotError(msg);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    if (e) e.preventDefault();
    if (!forgotOtp.trim()) {
      setForgotError("Please enter the verification code sent to your email.");
      return;
    }
    if (forgotNewPassword.length < 6) {
      setForgotError("Password must be at least 6 characters long.");
      return;
    }
    if (forgotNewPassword !== forgotConfirmPassword) {
      setForgotError("Passwords do not match. Please re-type your new password.");
      return;
    }

    setForgotLoading(true);
    setForgotError("");
    setForgotSuccess("");
    try {
      const res = await API.resetForgotPassword({
        email: forgotEmail,
        otp: forgotOtp.trim(),
        password: forgotNewPassword,
        password_confirmation: forgotConfirmPassword,
      });
      const data = res?.data || res;
      if (data?.success) {
        setForgotModalOpen(false);
        setPassword("");
        if (forgotIdentifier) setIdentifier(forgotIdentifier);
        await Swal.fire({
          icon: "success",
          title: "Password Reset Successful!",
          text: "Your password has been reset successfully. You can now sign in with your new password.",
          confirmButtonColor: "#0f3373",
        });
      } else {
        setForgotError(data?.message || "Could not reset password. Please check the OTP.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to reset password. Please verify the OTP.";
      setForgotError(msg);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    setLoginError("");
    setLoading(true);
    try {
      const result = await login(identifier, password);
      // API normally returns the payload directly. Accept a wrapped payload
      // too, so a proxy/interceptor cannot hide the dual-role selector.
      const payload = result?.data || result || {};
      if (payload.requires_role_selection || payload.code === "PSP-ROLE-SELECT") {
        setRoleSelectionData(payload);
        return;
      }

      if (payload.success) {
        const userRole = (payload.user?.role || "").toLowerCase();
        navigateForRole(userRole);
      } else {
        setLoginError(payload.message || "Invalid credentials. Please try again.");
        await Swal.fire({
          icon: "error",
          title: "Login Failed",
          text: result.message || "Invalid credentials. Please try again.",
          confirmButtonColor: "#0f3373", // Navy
        });
      }
    } catch (err) {
      console.error("Login error", err);
      setLoginError(err?.message || "Something went wrong during login. Please try again later.");
      await Swal.fire({
        icon: "error",
        title: "An Error Occurred",
        text: "Something went wrong during login. Please try again later.",
        confirmButtonColor: "#0f3373",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRole = async (targetRole) => {
    setLoading(true);
    try {
      const result = await login(identifier, password, targetRole);
      if (result.success) {
        setRoleSelectionData(null);
        const userRole = (result.user?.role || targetRole || "").toLowerCase();
        navigateForRole(userRole);
      } else {
        await Swal.fire({
          icon: "error",
          title: "Login Failed",
          text: result.message || "Could not log in with the chosen role.",
          confirmButtonColor: "#0f3373",
        });
      }
    } catch (err) {
      console.error("Role select login error", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to sign in. Please try again.",
        confirmButtonColor: "#0f3373",
      });
    } finally {
      setLoading(false);
    }
  };

  const resolvePhoto = (photoPath) => {
    if (!photoPath) return null;
    return resolveAssetUrl(photoPath);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col">
      <Navbar />
      <div className="flex-grow flex items-center justify-center p-6 sm:p-12 relative">
        <div className="w-full max-w-4xl mx-auto bg-white shadow-2xl rounded-2xl grid md:grid-cols-2 overflow-hidden border border-slate-150">
          {/* Left Panel (Branding) */}
          <div className="hidden md:flex flex-col items-center justify-center p-12 bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-900 text-white text-center relative overflow-hidden">
            {/* Elegant Background Circles */}
            <div className="absolute w-72 h-72 border border-slate-800 rounded-full -top-10 -left-10 opacity-30"></div>
            <div className="absolute w-96 h-96 border border-slate-800 rounded-full -bottom-20 -right-20 opacity-20"></div>

            <div className="relative z-10 flex flex-col items-center">
              <div className="p-3 bg-white/5 rounded-full border border-amber-500/30 mb-6 shadow-xl backdrop-blur-sm">
                <img
                  src="/assets/parasports.png"
                  alt="Parasports Logo"
                  className="w-24 h-24 object-contain filter drop-shadow-md"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/images/parasports.png"; // fallback
                  }}
                />
              </div>
              <h1 className="text-3xl font-extrabold tracking-tight mb-3 bg-gradient-to-r from-white via-slate-100 to-amber-400 bg-clip-text text-transparent">
                A Decade of Triumph
              </h1>
              <div className="w-16 h-1 bg-amber-500 rounded-full mb-4"></div>
              <p className="text-sm text-slate-300 max-w-xs leading-relaxed">
                Empowering Divyang Athletes, Supporting Dedication, and Inspiring Excellence.
              </p>
            </div>
          </div>

          {/* Right Panel (Form) */}
          <div className="p-8 sm:p-12 flex flex-col justify-center">
            <div className="mb-6">
              <h2 className="text-3xl font-extrabold text-slate-900">Welcome Back</h2>
              <p className="text-sm text-slate-500 mt-1">Sign in to manage and view your profile</p>

              {/* Coach access uses this shared login form. */}
              <div className="mt-3.5 p-2.5 bg-amber-50 border border-amber-200/80 rounded-xl">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                  <span className="text-xs font-bold text-amber-950">Appointed as Coach? Use these same credentials. You’ll choose Player or Coach after signing in.</span>
                </div>
              </div>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              {loginError && (
                <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-800">
                  {loginError}
                </div>
              )}
              <div>
                <label
                  htmlFor="identifier"
                  className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1"
                >
                  Email or Mobile
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3.5 text-slate-400">
                    <FaEnvelope size={14} />
                  </span>
                  <input
                    id="identifier"
                    type="text"
                    placeholder="you@example.com or 9876543210"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all text-slate-800 placeholder-slate-400 text-sm"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label
                    htmlFor="password"
                    className="block text-xs font-semibold text-slate-700 uppercase tracking-wider"
                  >
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setForgotModalOpen(true);
                      setForgotStep(1);
                      setForgotIdentifier(identifier || "");
                      setForgotError("");
                      setForgotSuccess("");
                    }}
                    className="text-xs font-semibold text-indigo-700 hover:text-indigo-950 hover:underline"
                  >
                    Forgot Password?
                  </button>
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-3.5 text-slate-400">
                    <FaLock size={14} />
                  </span>
                  <input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all text-slate-800 placeholder-slate-400 text-sm"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-3 bg-indigo-900 hover:bg-indigo-950 disabled:bg-indigo-400 text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all text-sm mt-2"
              >
                {loading && <FaSpinner className="animate-spin" />}
                {loading ? "Signing In..." : "Sign In"}
              </button>
            </form>

            <div className="text-center mt-8 pt-6 border-t border-slate-100">
              <p className="text-xs text-slate-500">
                Don't have an account?{" "}
                <Link
                  to="/register"
                  className="font-semibold text-indigo-700 hover:text-indigo-950 hover:underline"
                >
                  Register as Player
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Dual-Role Selection Modal */}
        {roleSelectionData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full border border-slate-200 overflow-hidden relative">
              {/* Header with gradient */}
              <div className="bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-900 text-white p-6 relative">
                <button
                  onClick={() => setRoleSelectionData(null)}
                  className="absolute top-4 right-4 text-slate-300 hover:text-white p-1 rounded-full hover:bg-white/10 transition"
                  title="Close and cancel"
                >
                  <FaTimes size={18} />
                </button>
                <div className="flex items-center gap-4">
                  {roleSelectionData.user?.photo ? (
                    <img
                      src={resolvePhoto(roleSelectionData.user.photo)}
                      alt={roleSelectionData.user?.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-amber-400 shadow-md"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/placeholder-avatar.png";
                      }}
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center border-2 border-amber-400 shadow-md">
                      <FaUserCircle className="text-4xl text-amber-300" />
                    </div>
                  )}
                  <div>
                    <span className="inline-block text-[11px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/30 mb-1">
                      Multiple Portal Access
                    </span>
                    <h3 className="text-xl font-bold text-white leading-tight">
                      {roleSelectionData.user?.name || "Welcome"}
                    </h3>
                    <p className="text-xs text-slate-300">
                      {roleSelectionData.user?.mobile || roleSelectionData.user?.email}
                    </p>
                  </div>
                </div>
              </div>

              {/* Body: Role Selection Cards */}
              <div className="p-6">
                <p className="text-sm font-semibold text-slate-700 mb-4 text-center">
                  This login has more than one available portal.
                  <br />
                  <span className="text-indigo-600">Which portal would you like to access today?</span>
                </p>

                <div className="space-y-3">
                  {(roleSelectionData.available_roles || []).map((option, index) => (
                    <button
                      key={option.role}
                      onClick={() => handleSelectRole(option.role)}
                      disabled={loading}
                      className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all text-left group shadow-sm hover:shadow-md ${index % 2 === 0 ? "border-indigo-100 hover:border-indigo-600 bg-indigo-50/50" : "border-emerald-100 hover:border-emerald-600 bg-emerald-50/50"}`}
                    >
                      <div className="flex items-center gap-3.5">
                        <div className={`w-12 h-12 rounded-xl text-white flex items-center justify-center shadow-md ${index % 2 === 0 ? "bg-indigo-600" : "bg-emerald-600"}`}>
                          {option.role === "player" ? <FaRunning className="text-2xl" /> : <FaUserShield className="text-2xl" />}
                        </div>
                        <div>
                          <div className="font-bold text-slate-950 text-base">{option.title || option.role}</div>
                          <p className="text-xs text-slate-500 mt-0.5">{option.description || "Open this portal"}</p>
                        </div>
                      </div>
                      <FaArrowRight className="text-slate-400 group-hover:text-slate-700 group-hover:translate-x-1 transition-all text-sm ml-2" />
                    </button>
                  ))}
                </div>

                {/* Footer Cancel / Switch */}
                <div className="mt-6 pt-4 border-t border-slate-100 flex justify-center">
                  <button
                    onClick={() => setRoleSelectionData(null)}
                    className="text-xs font-semibold text-slate-500 hover:text-slate-800 transition"
                  >
                    Switch account or sign in with different credentials
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Forgot Password Modal */}
        {forgotModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-100">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white p-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center border border-white/20">
                    <FaKey className="text-amber-400 text-lg" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">
                      Reset Password
                    </h3>
                    <p className="text-xs text-indigo-200">
                      {forgotStep === 1 ? "Verify registered identity" : "Enter OTP & new password"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setForgotModalOpen(false);
                    setForgotError("");
                    setForgotSuccess("");
                    setForgotStep(1);
                  }}
                  className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition"
                >
                  <FaTimes size={18} />
                </button>
              </div>

              {/* Coach note banner */}
              <div className="bg-amber-50 border-b border-amber-200/80 px-4 py-2.5 flex items-start gap-2">
                <span className="text-amber-600 font-bold text-sm leading-none mt-0.5">ℹ</span>
                <p className="text-xs text-amber-900 leading-tight">
                  <strong className="font-semibold">Appointed Coaches:</strong> Your coach account uses your player credentials. Resetting your password here automatically updates your access for both Player and Coach portals.
                </p>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                {forgotError && (
                  <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs font-semibold text-rose-800">
                    {forgotError}
                  </div>
                )}
                {forgotSuccess && (
                  <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs font-semibold text-emerald-800">
                    {forgotSuccess}
                  </div>
                )}

                {forgotStep === 1 ? (
                  <form onSubmit={handleSendForgotOtp} className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                        Registered Email, Mobile or Registration ID
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-3.5 text-slate-400">
                          <FaEnvelope size={14} />
                        </span>
                        <input
                          type="text"
                          required
                          value={forgotIdentifier}
                          onChange={(e) => {
                            setForgotIdentifier(e.target.value);
                            setForgotError("");
                          }}
                          placeholder="e.g. name@email.com or 9876543210 or AP-PAR-..."
                          className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition"
                        />
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1">
                        We will send a 6-digit verification code to the registered email address linked to your account.
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={forgotLoading}
                      className="w-full py-3 bg-gradient-to-r from-blue-900 to-indigo-800 hover:from-blue-800 hover:to-indigo-700 text-white font-bold rounded-lg shadow-md transition flex items-center justify-center gap-2 text-sm disabled:opacity-60"
                    >
                      {forgotLoading ? (
                        <>
                          <FaSpinner className="animate-spin text-sm" />
                          <span>Sending Verification Code...</span>
                        </>
                      ) : (
                        <>
                          <span>Send Verification OTP</span>
                          <FaArrowRight className="text-xs" />
                        </>
                      )}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 text-xs text-slate-600 flex items-center justify-between">
                      <div>
                        <span className="block text-[10px] uppercase font-bold text-slate-400">OTP Sent To</span>
                        <span className="font-semibold text-slate-800 font-mono">{forgotMaskedEmail}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setForgotStep(1);
                          setForgotError("");
                        }}
                        className="text-indigo-600 hover:underline text-xs font-semibold flex items-center gap-1"
                      >
                        <FaArrowLeft size={10} /> Change
                      </button>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                        6-Digit Verification Code (OTP)
                      </label>
                      <input
                        type="text"
                        required
                        maxLength={6}
                        value={forgotOtp}
                        onChange={(e) => {
                          setForgotOtp(e.target.value.replace(/\D/g, ""));
                          setForgotError("");
                        }}
                        placeholder="123456"
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-center font-mono text-lg tracking-widest text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                        New Password
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-3.5 text-slate-400">
                          <FaLock size={14} />
                        </span>
                        <input
                          type="password"
                          required
                          value={forgotNewPassword}
                          onChange={(e) => {
                            setForgotNewPassword(e.target.value);
                            setForgotError("");
                          }}
                          placeholder="At least 6 characters"
                          className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-3.5 text-slate-400">
                          <FaLock size={14} />
                        </span>
                        <input
                          type="password"
                          required
                          value={forgotConfirmPassword}
                          onChange={(e) => {
                            setForgotConfirmPassword(e.target.value);
                            setForgotError("");
                          }}
                          placeholder="Re-enter new password"
                          className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition"
                        />
                      </div>
                    </div>

                    <div className="pt-2 flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleSendForgotOtp}
                        disabled={forgotLoading}
                        className="px-3 py-2.5 border border-slate-200 text-slate-600 hover:bg-slate-100 rounded-lg text-xs font-semibold transition"
                      >
                        Resend OTP
                      </button>
                      <button
                        type="submit"
                        disabled={forgotLoading}
                        className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg shadow-md transition flex items-center justify-center gap-2 text-sm disabled:opacity-60"
                      >
                        {forgotLoading ? (
                          <>
                            <FaSpinner className="animate-spin text-sm" />
                            <span>Updating...</span>
                          </>
                        ) : (
                          <>
                            <FaCheckCircle className="text-xs" />
                            <span>Reset Password</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
